import { faWarning } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { CONFIRM_BUTTON_ID, IAppConfirmMessage } from "message/model"
import { messageSVC } from "message/service"
import { closeConfirmMessagePopup } from "message/tools/common"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { PopupTitle } from "ui/components/PopupTitle"

interface IConfirmMessageProps {
  confirmMessage: IAppConfirmMessage
}

export const ConfirmMessage = React.memo(
  ({
    confirmMessage: {
      onCancel,
      onSuccess,
      validateInput,
      onSuccessValidation,
      defaultInputValue,
      icon,
      title,
      message,
      cancelButtonLabel,
      children,
      confirmButtonLabel,
      hideCancelButton,
      inputPlaceholder,
      isInputMessage,
      isInputOptional,
      isInputPassword,
      isWarning,
      sx
    }
  }: IConfirmMessageProps) => {
    const [input, setInput] = React.useState(defaultInputValue || "")
    const [inputError, setInputError] = React.useState("")
    const [isSaving, setIsSaving] = React.useState(false)
    const onCancelFn = () => {
      closeConfirmMessagePopup()
      if (typeof onCancel === "function") {
        try {
          onCancel()
        } catch (error) {
          console.error("An error occured while executing the onCancel handler of the ConfirMessage component", error)
        }
      }
    }
    const onSuccessFn = async () => {
      if (inputError) {
        return
      }
      if (onSuccessValidation) {
        try {
          setIsSaving(true)
          const error = await onSuccessValidation(input)
          setIsSaving(false)
          if (error) {
            messageSVC.error(error)
            return
          }
        } catch (error) {
          setIsSaving(false)
          console.error(error)
          messageSVC.error(translate("user.logout.error.unexpected"))
          return
        }
      }
      closeConfirmMessagePopup()
      try {
        onSuccess(input)
      } catch (error) {
        console.error("An error occured while executing the onSuccess handler of the ConfirMessage component", error)
      }
    }
    return (
      <Dialog
        id="confirm-message"
        open={true}
        onClose={onCancelFn}
        aria-labelledby="form-dialog-title"
        sx={{
          padding: "1rem",
          ...sx
        }}>
        <DialogTitle
          id="confirm-message-title"
          sx={{
            minWidth: "20rem",
            color: theme => theme.palette.text.primary2
          }}>
          <PopupTitle icon={icon} title={title} id="confirm-message-title-text" />
        </DialogTitle>
        <DialogContent sx={{ pl: "2.5rem", pr: "2.5rem" }}>
          <FlexBox
            gap
            jc="space-between"
            sx={{
              "& .icon": {
                color: theme => theme.palette.primary.main,
                fontSize: "3rem"
              }
            }}>
            {isWarning && <FontAwesomeIcon icon={faWarning} className="icon" />}
            {Array.isArray(message) ? (
              <FlexBox vertical gap jc="flex-start" ai="flex-start">
                {message.map((m, index) => (
                  <DialogContentText
                    id={`confirm-message-text-${index}`}
                    color={isWarning ? "error" : "textPrimary"}
                    key={index}>
                    {m}
                  </DialogContentText>
                ))}
              </FlexBox>
            ) : (
              <DialogContentText id="confirm-message-text" color={isWarning ? "error" : "textPrimary"}>
                {message}
              </DialogContentText>
            )}
          </FlexBox>
          {isInputMessage && (
            <>
              <TextField
                id="confirm-message-input"
                variant="filled"
                type={isInputPassword ? "password" : "text"}
                autoFocus={!isInputOptional}
                margin="dense"
                defaultValue={input}
                onChange={event => {
                  const value = event.target.value
                  if (validateInput) {
                    setInputError(validateInput(value) || "")
                  }
                  setInput(value)
                }}
                onKeyUp={event => {
                  event.stopPropagation()
                  if (event.key === "Enter") {
                    if (!input && !isInputOptional) {
                      return
                    }
                    onSuccessFn()
                  }
                }}
                label={inputPlaceholder}
                fullWidth
              />
              {inputError && (
                <Typography color="error" fontSize=".9rem" fontWeight={400} sx={{ mt: "1rem", mb: "-1rem" }}>
                  {inputError}
                </Typography>
              )}
            </>
          )}
          {children}
        </DialogContent>
        <DialogActions sx={{ pl: "2.5rem", pr: "2.5rem", pb: "2.5rem" }}>
          {!hideCancelButton && (
            <Button id="confirm-message-cancel-button" variant="outlined" onClick={onCancelFn} color="primary">
              {cancelButtonLabel}
            </Button>
          )}
          <Button
            id={CONFIRM_BUTTON_ID}
            autoFocus={!isInputMessage}
            startIcon={isSaving && <CircularProgress size="1.5rem" />}
            disabled={
              isSaving ||
              (isInputMessage &&
                ((!input && !isInputOptional) || !!inputError || (!!defaultInputValue && defaultInputValue === input)))
            }
            onClick={() => onSuccessFn()}
            color="primary"
            variant="contained">
            {confirmButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)
